import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getStaffUsers } from "../api/ApiRoutesFile";

export const staffApiSlice = createApi({
  reducerPath: "staffApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Staffs"],
  endpoints: (builder) => ({
    // Change to match how you're querying the data
    staff: builder.query({
      query: ({ endpoint, lastId, params }) => {
        const url = lastId ? `${endpoint}${lastId}` : endpoint;
        return {
          url,
          method: "GET",
          params: params || undefined,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.staff.map(({ _id }) => ({
                type: "Staffs",
                id: _id,
              })),
              { type: "Staffs", id: "LIST" },
            ]
          : [{ type: "Staffs", id: "LIST" }],
    }),

    createStaff: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Optimistic update
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;

          // Only proceed with cache update if we have valid data
          if (data && data.staff && data.staff._id) {
            // This is the key part - update the specific query in the cache
            dispatch(
              staffApiSlice.util.updateQueryData(
                "staff",
                { endpoint: getStaffUsers, lastId },
                (draft) => {
                  // Make sure draft.staff exists before unshifting
                  if (draft && draft.staff) {
                    draft.staff.unshift(data.staff);
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Create staff failed:", error);
        }
      },
    }),

    updateStaff: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Fix the optimistic update
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          if (data && data.staff) {
            dispatch(
              staffApiSlice.util.updateQueryData(
                "staff", // Match the query name (singular)
                { endpoint: getStaffUsers, lastId }, // Match the parameters your query expects
                (draft) => {
                  if (draft && draft.staff) {
                    const staffIndex = draft.staff.findIndex(
                      (item) => item._id === id
                    );
                    if (staffIndex !== -1) {
                      draft.staff[staffIndex] = data.staff;
                    }
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update staff failed:", error);
        }
      },
    }),

    deleteStaff: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "DELETE",
      }),
      // Optimistic update for delete
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        // Get the current lastId from state if necessary
        // For this example, we're using a fixed lastId
        const lastId = 1;

        // Remove from lots list query
        const patchResult = dispatch(
          staffApiSlice.util.updateQueryData(
            "staff",
            { endpoint: getStaffUsers, lastId },
            (draft) => {
              if (draft && draft.staff) {
                draft.staff = draft.staff.filter((item) => {
                  return item._id !== id?.id;
                });
              }
            }
          )
        );

        try {
          // Wait for the actual API request to complete
          await queryFulfilled;
          // No need to invalidate tags - we've already updated the cache
        } catch (error) {
          // If the mutation fails, undo the optimistic update
          patchResult.undo();
          console.error("Delete lot failed:", error);
        }
      },
    }),
  }),
});

export const {
  useStaffQuery, // This should match your query name
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useDeleteStaffMutation,
} = staffApiSlice;
